// Derives the "next due" occurrence for a recurring deliverable parent row.
// Used to populate the DUE and STATUS columns on parent rows, which otherwise
// show "—" because the parent itself has no due date.

import type { Deliverable, DeliverableStatus } from "../../../../types";
import type { ChildRow } from "./groupDeliverables";

// Statuses that mean the deliverable cycle is complete — exclude these from
// the "next due" pick so an unresolved occurrence wins over a closed one.
// `rejected` is intentionally NOT here: a rejected occurrence still requires
// resubmission and is therefore an active obligation.
const COMPLETED_STATUSES: ReadonlySet<DeliverableStatus> = new Set([
  "accepted",
  "closed",
]);

function sortKey(d: Deliverable): string {
  return d.dueDate ?? d.occurrenceDate ?? "￿";
}

/**
 * Returns the most relevant occurrence to display on the parent row.
 *
 * Selection order:
 * 1. First non-completed occurrence sorted by dueDate ascending.
 * 2. If all occurrences are completed, falls back to the first by dueDate
 *    so old completed parents still show something.
 * 3. Returns null when there are no occurrences.
 */
export function deriveNextOccurrence(children: ChildRow[]): Deliverable | null {
  if (children.length === 0) return null;

  const sorted = [...children].sort((a, b) =>
    sortKey(a.deliverable).localeCompare(sortKey(b.deliverable)),
  );

  const active = sorted.find(
    (c) => !COMPLETED_STATUSES.has(c.deliverable.status),
  );
  return (active ?? sorted[0]).deliverable;
}
